/* ============================================================================
   Overview tab for the request-detail page.
   Renders the full creative brief (description, objectives, audience, format,
   creative direction, attachments) plus a staff-only internal notes thread.
   Registered via registerRequestTab(); other modules (Proofing / Hours /
   Comments) register their own tabs the same way.
   ============================================================================ */

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { registerRequestTab, type RequestTabProps } from '../../app/requestTabs';
import { apiGet, apiPost, isApiError } from '../../lib/api';
import { useAuth, hasRole } from '../../lib/auth';
import { useLookups, labelFor, statusPillClass, type RequestDetail, type AttachmentRow } from './data';

interface NoteRow {
  id: number;
  body: string;
  authorId: number;
  authorName: string;
  createdAt: string | null;
}

type AttachmentKind = 'image' | 'reference' | 'competitor';

/** Maximum decoded upload size (50 MB), matching the API's cap (attachments.ts). */
const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;

/** Read a File as base64 (strips the data: URL prefix). Same pattern as RequestForm.tsx / ProofingTab.tsx. */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('Could not read file.'));
    reader.readAsDataURL(file);
  });
}

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div className="mono" style={{ fontSize: 'var(--fs-xs)', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ink-3)', marginBottom: 3 }}>
        {label}
      </div>
      <div style={{ fontSize: 'var(--fs-base)', whiteSpace: 'pre-wrap' }}>{value}</div>
    </div>
  );
}

function Tags({ items }: { items: string[] }) {
  if (!items.length) return <span className="muted">—</span>;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {items.map((t) => (
        <span key={t} className="chip">
          {t}
        </span>
      ))}
    </div>
  );
}

function InternalNotes({ requestId }: { requestId: number }) {
  const [notes, setNotes] = useState<NoteRow[]>([]);
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(() => {
    apiGet<NoteRow[]>(`/api/requests/${requestId}/notes`)
      .then((r) => setNotes(Array.isArray(r) ? r : []))
      .catch(() => undefined);
  }, [requestId]);

  useEffect(() => {
    load();
  }, [load]);

  async function add(): Promise<void> {
    if (!body.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      await apiPost(`/api/requests/${requestId}/notes`, { body: body.trim() });
      setBody('');
      load();
    } catch (e) {
      setErr(isApiError(e) ? e.message : 'Could not add note.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <div className="card-header">
        <h3>Internal notes</h3>
      </div>
      <p className="meta" style={{ marginTop: 0 }}>
        Staff-only. Not shown to the requester.
      </p>
      <div className="field">
        <textarea
          className="in"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Add a triage note…"
          style={{ minHeight: 72 }}
        />
      </div>
      {err && <p className="error-text">{err}</p>}
      <button className="btn btn-navy btn-sm" disabled={busy || !body.trim()} onClick={() => void add()}>
        {busy ? 'Adding…' : 'Add note'}
      </button>

      <hr className="divider" />
      {notes.length === 0 ? (
        <p className="muted">No internal notes yet.</p>
      ) : (
        <div className="stack">
          {notes.map((n) => (
            <div key={n.id} style={{ borderLeft: '3px solid var(--line-strong)', paddingLeft: 12 }}>
              <div className="row" style={{ gap: 8 }}>
                <strong style={{ fontSize: 'var(--fs-sm)' }}>{n.authorName}</strong>
                <span className="meta">{n.createdAt?.replace('T', ' ') ?? ''}</span>
              </div>
              <div style={{ whiteSpace: 'pre-wrap', fontSize: 'var(--fs-sm)' }}>{n.body}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Attachments card: read-only file list for everyone, plus an "Add attachment"
 * upload control for the owner requester / assigned designer / admin (canAdd).
 * Owns its own row state (seeded from the parent's `initial` detail fetch) since
 * this tab has no way to trigger RequestDetail's reload — see RequestDetail.tsx.
 * Keyed by the parent on `detail.id` so switching requests reseeds `rows`.
 */
function AttachmentsCard({
  requestId,
  initial,
  canAdd,
}: {
  requestId: number;
  initial: AttachmentRow[];
  canAdd: boolean;
}) {
  const [rows, setRows] = useState<AttachmentRow[]>(initial);
  const [kind, setKind] = useState<AttachmentKind>('reference');
  const [files, setFiles] = useState<File[]>([]);
  const [fileKey, setFileKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  async function refresh(): Promise<void> {
    const r = await apiGet<AttachmentRow[]>(`/api/requests/${requestId}/attachments`);
    setRows(r);
  }

  async function upload(): Promise<void> {
    if (busy || files.length === 0) return;
    setBusy(true);
    setErr(null);
    setOkMsg(null);

    const toUpload = files;
    const succeeded: File[] = [];
    let stopReason: string | null = null;

    for (let i = 0; i < toUpload.length; i++) {
      const f = toUpload[i];
      setProgress(`Uploading ${i + 1} of ${toUpload.length}: ${f.name}`);
      if (f.size > MAX_UPLOAD_BYTES) {
        stopReason = `"${f.name}" is larger than the 50 MB limit — not uploaded.`;
        break;
      }
      try {
        const dataBase64 = await fileToBase64(f);
        await apiPost(`/api/requests/${requestId}/attachments`, {
          filename: f.name,
          kind,
          contentType: f.type || undefined,
          sizeBytes: f.size,
          dataBase64,
        });
        succeeded.push(f);
      } catch (e) {
        stopReason = isApiError(e) ? e.message : `Could not upload "${f.name}".`;
        break;
      }
    }

    setProgress(null);
    let finalErr = stopReason;
    if (succeeded.length > 0) {
      // Drop only the files that made it, so a retry never re-sends them.
      setFiles((prev) => prev.filter((x) => !succeeded.includes(x)));
      setFileKey((k) => k + 1);
      try {
        await refresh();
        setOkMsg(`Added ${succeeded.length} file(s).`);
      } catch {
        finalErr = finalErr ?? 'Uploaded, but the list could not be refreshed — reload the page.';
      }
    }
    setErr(finalErr);
    setBusy(false);
  }

  return (
    <div className="card">
      <div className="card-header">
        <h3>Attachments</h3>
      </div>
      {rows.length === 0 ? (
        <p className="muted">No files attached.</p>
      ) : (
        <div className="table-wrap">
          <table className="tbl tbl-compact">
            <thead>
              <tr>
                <th>File</th>
                <th>Kind</th>
                <th>Added</th>
                <th aria-label="Open" />
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.id}>
                  <td>
                    {a.downloadUrl ? (
                      <a href={a.downloadUrl} target="_blank" rel="noreferrer">
                        {a.filename}
                      </a>
                    ) : (
                      a.filename
                    )}
                  </td>
                  <td style={{ textTransform: 'capitalize' }}>{a.kind ?? '—'}</td>
                  <td className="mono">{a.uploadedAt?.slice(0, 10) ?? '—'}</td>
                  <td style={{ textAlign: 'right' }}>
                    {a.downloadUrl ? (
                      <a className="btn btn-sm btn-ghost" href={a.downloadUrl} target="_blank" rel="noreferrer">
                        Open
                      </a>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {canAdd && (
        <>
          <hr className="divider" />
          <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div className="field" style={{ marginBottom: 0, minWidth: 130 }}>
              <label>Kind</label>
              <select
                className="in"
                value={kind}
                disabled={busy}
                onChange={(e) => setKind(e.target.value as AttachmentKind)}
              >
                <option value="image">Image</option>
                <option value="reference">Reference</option>
                <option value="competitor">Competitor</option>
              </select>
            </div>
            <div className="field" style={{ marginBottom: 0, flex: 1, minWidth: 200 }}>
              <label>Add attachment</label>
              <input
                key={fileKey}
                type="file"
                multiple
                className="in"
                style={{ padding: 8, height: 'auto' }}
                disabled={busy}
                onChange={(e) => setFiles(e.target.files ? Array.from(e.target.files) : [])}
              />
            </div>
            <button className="btn btn-navy btn-sm" disabled={busy || files.length === 0} onClick={() => void upload()}>
              {busy ? 'Uploading…' : 'Upload'}
            </button>
          </div>
          <p className="meta" style={{ marginTop: 6 }}>
            Any file type, up to 50 MB each. Files are added immediately.
          </p>
          {progress && <p className="meta">{progress}</p>}
          {err && <p className="error-text">{err}</p>}
          {!err && okMsg && <p className="meta">{okMsg}</p>}
        </>
      )}
    </div>
  );
}

function OverviewTab({ requestId }: RequestTabProps) {
  const { user } = useAuth();
  const { lookups } = useLookups();
  const [detail, setDetail] = useState<RequestDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    apiGet<RequestDetail>(`/api/requests/${requestId}`)
      .then((d) => {
        if (active) setDetail(d);
      })
      .catch((e) => {
        if (active) setError(isApiError(e) ? e.message : 'Could not load the brief.');
      });
    return () => {
      active = false;
    };
  }, [requestId]);

  const staff = hasRole(user, ['Admin', 'SystemAdmin', 'Designer']);

  if (error) {
    return (
      <div className="card empty">
        <h3>Couldn't load the brief</h3>
        <p className="muted">{error}</p>
      </div>
    );
  }
  if (!detail) {
    return (
      <div className="card" style={{ display: 'grid', placeItems: 'center', padding: 40 }}>
        <div className="spinner" />
      </div>
    );
  }

  const audience = [detail.targetAudience, detail.audienceAge, detail.audienceGender, detail.audienceOther]
    .filter((x) => x && x.trim())
    .join(' · ');

  // Who may attach files from this card — mirrors the API's own gate (attachments.ts):
  // the owner requester, the assigned designer, or an admin. Everyone else stays read-only.
  const isAdmin = hasRole(user, ['Admin', 'SystemAdmin']);
  const isOwnerRequester = user?.role === 'Requester' && detail.requesterId === user.id;
  const isAssignedDesigner = user?.role === 'Designer' && detail.designerId === user.id;
  const canAddAttachment = isAdmin || isOwnerRequester || isAssignedDesigner;

  return (
    <div className="grid" style={{ gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)' }}>
      <div className="stack">
        <div className="card">
          <div className="card-header">
            <h3>Brief</h3>
          </div>
          <Field label="Description" value={detail.description || <span className="muted">—</span>} />
          <Field label="Business objectives" value={<Tags items={detail.objectives.map((c) => labelFor(lookups.objectives, c))} />} />
          <Field label="Call to action" value={detail.cta || <span className="muted">—</span>} />
          <Field label="Success metrics" value={<Tags items={detail.metrics.map((c) => labelFor(lookups.metrics, c))} />} />
          <Field label="Target audience" value={audience || <span className="muted">—</span>} />
          <Field label="Content / copy" value={detail.contentCopy || <span className="muted">—</span>} />
        </div>

        <div className="card">
          <div className="card-header">
            <h3>Deliverables</h3>
          </div>
          <Field label="Collateral type" value={detail.collateralTypeName || <span className="muted">—</span>} />
          <Field label="Formats" value={<Tags items={detail.formats.map((c) => labelFor(lookups.formats, c))} />} />
          <Field label="Sizes" value={detail.sizes || <span className="muted">—</span>} />
          <Field label="Marketing channels" value={<Tags items={detail.channels.map((c) => labelFor(lookups.channels, c))} />} />
        </div>

        <div className="card">
          <div className="card-header">
            <h3>Creative direction</h3>
          </div>
          <Field label="Campaign theme" value={detail.campaignTheme || <span className="muted">—</span>} />
          <Field label="Design style" value={detail.designStyleName || <span className="muted">—</span>} />
          <Field label="Preferred colors" value={detail.preferredColors || <span className="muted">—</span>} />
          <Field label="Preferred imagery" value={detail.preferredImagery || <span className="muted">—</span>} />
          <Field label="Additional notes" value={detail.notes || <span className="muted">—</span>} />
        </div>

        <AttachmentsCard
          key={detail.id}
          requestId={requestId}
          initial={detail.attachments}
          canAdd={canAddAttachment}
        />
      </div>

      <div className="stack">
        {staff ? (
          <InternalNotes requestId={requestId} />
        ) : (
          <div className="card">
            <div className="card-header">
              <h3>Status</h3>
            </div>
            <span className={`pill ${statusPillClass(detail.statusCode)}`}>
              {detail.statusName}
            </span>
            <hr className="divider" />
            <p className="meta">Design status is set by the assigned designer.</p>
          </div>
        )}
      </div>
    </div>
  );
}

registerRequestTab({
  id: 'overview',
  label: 'Overview',
  order: 10,
  component: OverviewTab,
});
